import React, { useEffect, useState } from "react";
import { Card, Empty } from "./TvBits";

const COLORS = ["#00F0FF", "#F472B6", "#A78BFA", "#10B981", "#F59E0B"];
const usd = (v, d = 0) => `${v < 0 ? "-" : ""}$${Math.abs(v || 0).toLocaleString("en-US", { maximumFractionDigits: d, minimumFractionDigits: d })}`;
const pc = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(2)}%`);
const col = (v) => (v == null ? "#94a3b8" : v >= 0 ? "#10B981" : "#F43F5E");

const Donut = ({ parts }) => {
  let a = 0;
  const R = 90, C = 2 * Math.PI * R;
  return (
    <svg viewBox="0 0 240 240" className="w-[clamp(12rem,22vw,24rem)] h-auto" data-testid="ira-donut">
      {parts.map((p, i) => {
        const len = (p.pct / 100) * C;
        const el = <circle key={p.name} cx="120" cy="120" r={R} fill="none" stroke={COLORS[i % 5]} strokeWidth="34"
          strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-a} transform="rotate(-90 120 120)" />;
        a += len;
        return el;
      })}
    </svg>
  );
};

const Spark = ({ pts }) => {
  if (!pts || pts.length < 2) return <span className="text-cyan-100/30">—</span>;
  const lo = Math.min(...pts), hi = Math.max(...pts), w = 120, h = 32;
  const d = pts.map((v, i) => `${(i / (pts.length - 1)) * w},${h - ((v - lo) / (hi - lo || 1)) * h}`).join(" ");
  return <svg viewBox={`0 0 ${w} ${h}`} className="w-28 h-8"><polyline points={d} fill="none" strokeWidth="2.5" stroke={pts[pts.length - 1] >= pts[0] ? "#10B981" : "#F43F5E"} /></svg>;
};

const Slide1 = ({ d }) => (
  <div className="h-full grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-8 items-center" data-testid="ira-slide-1">
    <div className="min-w-0">
      <p className="nx-mono tracking-[0.3em] text-cyan-300/60 text-[clamp(0.8rem,1vw,1.2rem)]">ROTH IRA · ONE ACCOUNT · TOTAL VALUE</p>
      <p className="nx-orbitron font-black text-white text-[clamp(3rem,7vw,8rem)] leading-none mt-2" data-testid="ira-total">{usd(d.total_value)}</p>
      <p className="nx-orbitron font-bold text-[clamp(1.5rem,2.6vw,3rem)] mt-4" style={{ color: col(d.day_pl_usd) }} data-testid="ira-day-pl">
        {d.day_pl_usd >= 0 ? "▲" : "▼"} {usd(d.day_pl_usd)} ({pc(d.day_pl_pct)}) today</p>
      <p className="text-[clamp(1rem,1.4vw,1.6rem)] text-cyan-100/70 mt-3">Total gain {usd(d.total_gain_usd)} {d.total_gain_pct != null && `(${pc(d.total_gain_pct)})`} · cost basis known on {d.cost_basis_coverage}</p>
    </div>
    <div className="flex items-center gap-6 min-w-0">
      <Donut parts={d.by_sector.slice(0, 5)} />
      <div className="space-y-3 min-w-0">{d.by_sector.slice(0, 5).map((b, i) => (
        <div key={b.name} className="flex items-center gap-3"><span className="w-4 h-4 rounded-full shrink-0" style={{ background: COLORS[i % 5] }} />
          <p className="text-[clamp(1rem,1.5vw,1.75rem)] text-cyan-50 truncate">{b.name} <b>{b.pct}%</b> <span className="text-cyan-100/50">{usd(b.value)}</span></p></div>
      ))}</div>
    </div>
  </div>
);

const Slide2 = ({ d }) => (
  <table className="w-full text-[clamp(0.95rem,1.35vw,1.6rem)]" data-testid="ira-slide-2">
    <thead><tr className="nx-mono text-cyan-300/60 text-left tracking-widest text-[0.75em]"><th className="py-1">SYMBOL</th><th className="text-right">VALUE</th><th className="text-right">DAY</th><th className="text-right">TOTAL</th><th className="pl-6">30D</th></tr></thead>
    <tbody>{d.holdings.slice(0, 10).map((h) => (
      <tr key={h.symbol} className="border-t border-cyan-300/10" data-testid={`ira-row-${h.symbol}`}>
        <td className="py-[0.35em] nx-orbitron font-bold text-white">{h.symbol}</td>
        <td className="text-right text-cyan-50">{usd(h.value)}</td><td className="text-right font-bold" style={{ color: col(h.day_pct) }}>{pc(h.day_pct)}</td>
        <td className="text-right" style={{ color: col(h.gain_pct) }}>{pc(h.gain_pct)}</td><td className="pl-6"><Spark pts={h.spark} /></td>
      </tr>))}</tbody>
  </table>
);

const Slide3 = ({ d }) => (
  <div className="h-full grid grid-cols-2 gap-8 min-h-0" data-testid="ira-slide-3">
    <div className="min-h-0">
      <p className="nx-mono tracking-[0.3em] text-cyan-300/60 mb-3">BIGGEST MOVERS TODAY</p>
      {d.movers.map((m) => (
        <div key={m.symbol} className="flex justify-between items-baseline border-t border-cyan-300/10 py-2 text-[clamp(1.1rem,1.7vw,2rem)]">
          <span className="nx-orbitron font-bold text-white">{m.symbol}</span>
          <span className="font-bold" style={{ color: col(m.day_pct) }}>{pc(m.day_pct)}</span></div>))}
    </div>
    <div className="min-h-0 overflow-hidden">
      <p className="nx-mono tracking-[0.3em] text-pink-300/70 mb-3">NEXUS IRA ALERTS</p>
      {d.alerts.length ? d.alerts.slice(0, 4).map((a) => (
        <div key={a.id} className="nx-glass rounded-2xl px-4 py-3 mb-3" data-testid={`ira-alert-${a.id}`}>
          <p className="whitespace-pre-line text-[clamp(0.95rem,1.3vw,1.5rem)] text-cyan-50 line-clamp-3">{a.text}</p></div>)) : <Empty>No alerts today — portfolio is quiet.</Empty>}
    </div>
  </div>
);

export const IraScene = ({ d: all }) => {
  const d = all.ira;
  const [k, setK] = useState(0);
  useEffect(() => { const t = setInterval(() => setK((x) => (x + 1) % 3), 13000); return () => clearInterval(t); }, []);
  if (!d) return <Card title="ROTH IRA" className="h-full"><Empty>No IRA holdings imported yet.</Empty></Card>;
  const S = [Slide1, Slide2, Slide3][k];
  return (
    <Card title="ROTH IRA" code={`${d.holdings.length} HOLDINGS · ${k + 1}/3`} className="h-full" testid="tv-scene-ira">
      <div className="h-full flex flex-col min-h-0">
        <div key={k} className="flex-1 min-h-0 nx-fade-up"><S d={d} /></div>
        <p className="nx-mono text-[11px] tracking-widest text-cyan-100/40 pt-2" data-testid="ira-footer">Live data: {d.source} · Not financial advice</p>
      </div>
    </Card>
  );
};
